import { Button } from '@/components/ui/button';
import { Calendar, ChevronDown } from 'lucide-react';
import React, { useEffect, useState } from 'react';

const Popover = ({ children }: { children: React.ReactNode }) => {
  return <div className="relative">{children}</div>;
};

const PopoverTrigger = ({ children, onClick }: { children: React.ReactNode; onClick: (e: React.MouseEvent) => void }) => {
  return <div onClick={onClick}>{children}</div>;
};

const PopoverContent = ({ children, open, className = "" }: { children: React.ReactNode; open: boolean; className?: string }) => {
  if (!open) return null;

  return (
    <div className={`absolute z-50 mt-2 w-auto p-4 bg-white border border-gray-200 rounded-lg shadow-lg ${className}`}>
      {children}
    </div>
  );
};

// Select component
const Select = ({ value, onValueChange, children, placeholder = "Select..." }: { value: string; onValueChange: (value: string) => void; children: (props: { onValueChange: (value: string) => void; setOpen: (open: boolean) => void }) => React.ReactNode; placeholder?: string }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={(e) => { e.preventDefault(); setOpen(!open) }}
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span>{value || placeholder}</span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-white shadow-md">
          <div onClick={() => setOpen(false)}>
            {children({ onValueChange, setOpen })}
          </div>
        </div>
      )}
    </div>
  );
};

const SelectItem = ({ value, children, onSelect, setOpen }: { value: string; children: React.ReactNode; onSelect: (value: string) => void; setOpen: (open: boolean) => void }) => (
  <div
    className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
    onClick={() => {
      onSelect(value);
      setOpen(false);
    }}
  >
    {children}
  </div>
);


interface CalendarComponentProps {
  selected?: Date;
  onSelect: (date: Date) => void;
  className?: string;
}

const CalendarComponent = ({ selected, onSelect, className = "" }: CalendarComponentProps) => {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(selected?.getFullYear() || today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(selected?.getMonth() || today.getMonth());

  // Generate years (from 1900 to current year + 10)
  const years: number[] = [];
  for (let year = new Date().getFullYear() + 10; year >= 1900; year--) {
    years.push(year);
  }

  const monthNames = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  const dayNames = ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"];

  // Calculate calendar days
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const firstDayOfWeek = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();

  const selectDate = (day: number) => {
    const selectedDate = new Date(currentYear, currentMonth, day);
    onSelect(selectedDate);
  };

  const isToday = (day: number) => {
    return today.getDate() === day &&
      today.getMonth() === currentMonth &&
      today.getFullYear() === currentYear;
  };

  const isSelected = (day: number) => {
    if (!selected) return false;
    return selected.getDate() === day &&
      selected.getMonth() === currentMonth &&
      selected.getFullYear() === currentYear;
  };

  // Generate calendar days
  const calendarDays = [];

  // Previous month's trailing days
  const prevMonthDays = new Date(currentYear, currentMonth, 0).getDate();
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    calendarDays.push({
      day: prevMonthDays - i,
      isCurrentMonth: false,
      isPrevMonth: true
    });
  }

  // Current month days
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push({
      day,
      isCurrentMonth: true,
      isPrevMonth: false
    });
  }

  // Next month's leading days
  const remainingSlots = 42 - calendarDays.length;
  for (let day = 1; day <= remainingSlots; day++) {
    calendarDays.push({
      day,
      isCurrentMonth: false,
      isPrevMonth: false
    });
  }

  return (
    <div className={`p-4 w-80 ${className}`}>
      {/* Year and Month Dropdowns */}
      <div className="flex gap-2 mb-4">
        <div className="flex-1">
          <Select
            value={monthNames[currentMonth]}
            onValueChange={() => { }}
            placeholder="Month"
          >
            {({ onValueChange, setOpen }) => (
              <div>
                {monthNames.map((month, index) => (
                  <SelectItem
                    key={month}
                    value={month}
                    onSelect={() => setCurrentMonth(index)}
                    setOpen={setOpen}
                  >
                    {month}
                  </SelectItem>
                ))}
              </div>
            )}
          </Select>
        </div>
        <div className="flex-1">
          <Select
            value={currentYear.toString()}
            onValueChange={() => { }}
            placeholder="Year"
          >
            {({ onValueChange, setOpen }) => (
              <div>
                {years.map((year) => (
                  <SelectItem
                    key={year}
                    value={year.toString()}
                    onSelect={() => setCurrentYear(year)}
                    setOpen={setOpen}
                  >
                    {year}
                  </SelectItem>
                ))}
              </div>
            )}
          </Select>
        </div>
      </div>

      {/* Day names */}
      <div className="grid grid-cols-7 mb-2">
        {dayNames.map((day) => (
          <div key={day} className="text-xs font-medium text-gray-500 text-center p-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((dateObj, index) => (
          <Button
            key={index}
            variant="ghost"
            size="icon"
            className={`
              h-9 w-9 text-sm
              ${!dateObj.isCurrentMonth ? 'text-gray-300 hover:text-gray-400' : ''}
              ${isToday(dateObj.day) && dateObj.isCurrentMonth ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
              ${isSelected(dateObj.day) && dateObj.isCurrentMonth ? 'bg-gray-900 text-white hover:bg-gray-800' : ''}
            `}
            onClick={(e) => { e.preventDefault(); dateObj.isCurrentMonth && selectDate(dateObj.day) }}
            disabled={!dateObj.isCurrentMonth}
          >
            {dateObj.day}
          </Button>
        ))}
      </div>
    </div>
  );
};

interface DatePickerProps {
  date?: Date;
  onSelect: (date: Date) => void;
}

export default function DatePicker({ date, onSelect }: DatePickerProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const formatDate = (date?: Date) => {
    if (!date) return "Seleccione una fecha";
    return date.toLocaleDateString("es-AR", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const handleDateSelect = (selectedDate: Date) => {
    onSelect(selectedDate);
    setOpen(false);
  };

  return (
    <Popover>
      <PopoverTrigger onClick={(e) => { e.preventDefault(); setOpen(!open) }}>
        <Button
          variant="outline"
          className="w-full justify-start text-left font-normal"
        >
          <Calendar className="mr-2 h-4 w-4" />
          {date && formatDate(date) || "Seleccione una fecha"}
        </Button>
      </PopoverTrigger>
      <PopoverContent open={open} className="w-auto p-0">
        <CalendarComponent
          selected={date}
          onSelect={handleDateSelect}
        />
      </PopoverContent>
    </Popover>
  );
}